// src/pages/ProductList.js
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../assets/css/print-products.css";
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Table,
} from "reactstrap";
import TransferItemsModal from "../modals/transferModal";
import PrintContainer from "components/Print/PrintContainer";
import BarcodeScanner from "components/Barcode/ScannerCode";
import FilterForm from "./../components/Forms/FilterForms";
import NotificationComponent from "../components/Alert/alert";
import { useTranslation } from "../hooks/useTranslation";

// RTK Query hooks
import {
  useFetchProductsByTypeQuery,
  useFetchLocationsQuery,
} from "../api/api";

const ProductList = () => {
  const t = useTranslation();
  // Refs
  const notificationComponentRef = useRef(null);
  const lastNotified = useRef({ products: false, locations: false });

  const [printProducts, setPrintProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [transferModalOpen, setTransferModalOpen] = useState(false);

  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [codeFilter, setCodeFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");

  // Columns
  const columns = [
    "id",
    "brand",
    "color",
    "number",
    "specific_type",
    "code",
    "location",
    "price",
  ];
  const tableHeaders = [
    t("table.id"),
    t("table.brand"),
    t("table.color"),
    t("table.number"),
    t("table.type"),
    t("table.code"),
    t("table.locations"),
    t("table.price"),
  ];

  // RTK Query
  const {
    data: productsByType = [],
    isFetching: isFetchingProducts,
    isError: isProductsError,
    error: productsError,
  } = useFetchProductsByTypeQuery("productList");

  const {
    data: locations = [],
    isFetching: isFetchingLocations,
    isError: isLocationsError,
    error: locationsError,
  } = useFetchLocationsQuery();

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (
      !initialized &&
      Array.isArray(productsByType) &&
      productsByType.length
    ) {
      // start with nothing selected, user filters/chooses later
      setFilteredProducts([]);
      setPrintProducts([]);
      setInitialized(true);
    }
  }, [initialized, productsByType]);

  // Edge-triggered error notification (depend only on flags)
  useEffect(() => {
    const key = isProductsError
      ? "products"
      : isLocationsError
      ? "locations"
      : null;

    if (key && !lastNotified.current[key]) {
      const msg =
        productsError?.data?.message ||
        locationsError?.data?.message ||
        "Error on fetching data";
      notificationComponentRef.current?.showNotification(msg, "danger");
      lastNotified.current[key] = true;
    }

    if (!isProductsError && !isLocationsError) {
      lastNotified.current = { products: false, locations: false };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProductsError, isLocationsError]);

  const openTransferModal = () => {
    if (filteredProducts.length !== 0) {
      setTransferModalOpen(true);
    } else {
      notificationComponentRef.current?.showNotification(
        t("products.selectProducts"),
        "danger"
      );
    }
  };

  // Handlers
  const handleProductSelection = (productId) => {
    const updatedPrintProducts = printProducts.map((product) =>
      product.id === productId
        ? { ...product, selected: !product.selected }
        : product
    );
    setPrintProducts(updatedPrintProducts);
    setFilteredProducts(updatedPrintProducts);
  };

  const handleStartDateChange = (e) => setStartDate(e.target.value);
  const handleEndDateChange = (e) => setEndDate(e.target.value);
  const handleCodeFilterChange = (e) => setCodeFilter(e.target.value);
  const handleBrandFilterChange = (e) => setBrandFilter(e.target.value);

  const handleBarcodeData = (filtered) => {
    const existingIds = filteredProducts.map((item) => item.id);
    const newItems = filtered.filter((item) => !existingIds.includes(item.id));
    const updated = [...filteredProducts, ...newItems];
    setFilteredProducts(updated);
    setPrintProducts(updated);
  };

  const handleAllProductSelection = () => {
    const allSelected = printProducts.every((p) => p.selected);
    const updated = printProducts.map((p) => ({
      ...p,
      selected: !allSelected,
    }));
    setFilteredProducts(updated);
    setPrintProducts(updated);
  };

  const selectAll = () => {
    // use productsByType directly (no local mirror)
    setFilteredProducts(productsByType);
    setPrintProducts(productsByType);
  };

  const handleBarcodeScanned = (scannedValue) => {
    const syntheticEvent = {
      preventDefault: () => {},
      target: { value: scannedValue, barcode: scannedValue },
    };
    handleSubmit(syntheticEvent);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const scannedCode = event?.target?.barcode;
    const startDateObject = startDate ? new Date(startDate) : null;
    const endDateObject = endDate ? new Date(endDate) : null;

    const startOfDay = startDateObject ? new Date(startDateObject) : null;
    if (startOfDay) startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = endDateObject ? new Date(endDateObject) : null;
    if (endOfDay) endOfDay.setHours(23, 59, 59, 999);

    const filtered = (productsByType || []).filter((product) => {
      const productDate = new Date(product.date);
      const startOk = !startOfDay || productDate >= startOfDay;
      const endOk = !endOfDay || productDate <= endOfDay;

      const codeOk =
        !codeFilter ||
        String(product.code || "")
          .toLowerCase()
          .includes(codeFilter.toLowerCase());
      const brandOk =
        !brandFilter ||
        String(product.brand || "")
          .toLowerCase()
          .includes(brandFilter.toLowerCase());

      if (scannedCode) return scannedCode === product.barcode;
      return startOk && endOk && codeOk && brandOk;
    });

    if (!scannedCode) {
      setFilteredProducts(filtered);
      setPrintProducts(filtered);
    } else {
      handleBarcodeData(filtered);
    }
  };

  return (
    <>
      <div className="content">
        <BarcodeScanner onBarcodeScanned={handleBarcodeScanned} />
        <NotificationComponent ref={notificationComponentRef} />

        <Card>
          <CardHeader>
            <Row>
              <Col className="text-left" sm="6">
                <h5 className="card-category">CMS</h5>
                <CardTitle tag="h2">{t("products.title")}</CardTitle>
              </Col>
              <Col className="text-right" sm="6">
                <Link to="/admin/catalog/products" className="btn btn-link">
                  Open Catalog Operations
                </Link>
                <PrintContainer printProducts={printProducts} />
              </Col>
              <Col sm="12">
                <Alert color="secondary" className="mb-4">
                  New product management now lives in{" "}
                  <Link to="/admin/catalog/products">Catalog Operations</Link>.
                  This page remains available for legacy inventory reads during
                  the transition.
                </Alert>
                <FilterForm
                  startDate={startDate}
                  endDate={endDate}
                  codeFilter={codeFilter}
                  brandFilter={brandFilter}
                  handleStartDateChange={handleStartDateChange}
                  handleEndDateChange={handleEndDateChange}
                  handleCodeFilterChange={handleCodeFilterChange}
                  handleBrandFilterChange={handleBrandFilterChange}
                  handleSubmit={handleSubmit}
                  openTransferModal={openTransferModal}
                  selectAll={selectAll}
                />
              </Col>
            </Row>
          </CardHeader>

          <CardBody>
            {isFetchingProducts || isFetchingLocations ? (
              <></>
            ) : isProductsError || isLocationsError ? (
              <div className="p-3">{t("messages.failedToLoad")}</div>
            ) : (
              <Table className="tablesorter productsTable" responsive>
                {filteredProducts.length > 0 ? (
                  <thead className="text-primary">
                    <tr>
                      {tableHeaders.map((header, i) => (
                        <th key={i}>{header}</th>
                      ))}
                      <th>
                        <input
                          type="checkbox"
                          checked={
                            printProducts.length > 0 &&
                            printProducts.every((p) => p.selected)
                          }
                          onChange={handleAllProductSelection}
                        />
                      </th>
                    </tr>
                  </thead>
                ) : (
                  ""
                )}

                <tbody>
                  {filteredProducts.map((product, index) => (
                    <tr key={index}>
                      {columns.map((key, idx) => (
                        <td className={key} key={idx}>
                          {String(product[key] ?? "")}
                        </td>
                      ))}
                      <td>
                        <input
                          type="checkbox"
                          checked={
                            !!printProducts.find((p) => p.id === product.id)
                              ?.selected
                          }
                          onChange={() => handleProductSelection(product.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>

        <TransferItemsModal
          modalOpen={transferModalOpen}
          notificationComponentRef={notificationComponentRef}
          toggleModal={() => setTransferModalOpen(!transferModalOpen)}
          productsData={printProducts}
          locationsData={locations}
          setFilteredProducts={setFilteredProducts}
        />
      </div>
    </>
  );
};

export default ProductList;
